import React, { useState } from 'react';
import CodeBlock from '../components/CodeBlock';
import './ComponentPage.css';

const COMPONENTS_DATA = {
  text: {
    name: 'Text',
    description: 'A customizable text component with theme support and font weight options.',
    props: [
      { name: 'children', type: 'React.ReactNode', required: true, description: 'Text content' },
      { name: 'mode', type: "'regular' | 'bold' | 'medium'", required: false, description: 'Font weight style', default: "'regular'" },
      { name: 'fontSize', type: 'string', required: false, description: 'Font size preset', default: "'medium'" },
      { name: 'colored', type: 'boolean', required: false, description: 'Use primary color', default: 'false' },
      { name: 'style', type: 'TextStyle', required: false, description: 'Custom styles' },
    ],
    examples: [
      {
        title: 'Basic Text',
        code: `<Text>Hello World</Text>`,
      },
      {
        title: 'Bold Text',
        code: `<Text mode="bold">Bold Text</Text>`,
      },
      {
        title: 'Large Colored Text',
        code: `<Text fontSize="large" colored mode="bold">
  Featured Title
</Text>`,
      },
    ],
  },
  button: {
    name: 'Button',
    description: 'A versatile button component with multiple types, loading states, and icon support.',
    props: [
      { name: 'title', type: 'string', required: true, description: 'Button text' },
      { name: 'onPress', type: '() => void', required: true, description: 'Callback when pressed' },
      { name: 'type', type: "'contained' | 'outline' | 'text'", required: false, description: 'Button style', default: "'contained'" },
      { name: 'radius', type: "'xl' | 'l' | 'm' | 's'", required: false, description: 'Border radius size', default: "'s'" },
      { name: 'color', type: 'string', required: false, description: 'Button color' },
      { name: 'disabled', type: 'boolean', required: false, description: 'Disable button', default: 'false' },
      { name: 'loading', type: 'boolean', required: false, description: 'Show loading', default: 'false' },
    ],
    examples: [
      {
        title: 'Basic Button',
        code: `<Button
  title="Click Me"
  onPress={() => console.log('Clicked')}
/>`,
      },
      {
        title: 'Outlined Button',
        code: `<Button
  title="Outline"
  type="outline"
  onPress={() => {}}
/>`,
      },
      {
        title: 'Loading State',
        code: `<Button
  title="Loading..."
  loading={true}
  disabled={true}
  onPress={() => {}}
/>`,
      },
    ],
  },
  textinput: {
    name: 'TextInput',
    description: 'A customizable text input with error handling and theme support.',
    props: [
      { name: 'value', type: 'string', required: true, description: 'Input value' },
      { name: 'onChangeText', type: '(text: string) => void', required: true, description: 'Change callback' },
      { name: 'placeholder', type: 'string', required: false, description: 'Placeholder text' },
      { name: 'disabled', type: 'boolean', required: false, description: 'Disable input', default: 'false' },
      { name: 'isError', type: 'boolean', required: false, description: 'Show error state', default: 'false' },
      { name: 'errorMessage', type: 'string', required: false, description: 'Error message' },
    ],
    examples: [
      {
        title: 'Basic Input',
        code: `const [text, setText] = useState('');
<TextInput
  placeholder="Enter text"
  value={text}
  onChangeText={setText}
/>`,
      },
      {
        title: 'With Error',
        code: `<TextInput
  placeholder="Email"
  isError={true}
  errorMessage="Invalid email"
/>`,
      },
    ],
  },
  switch: {
    name: 'Switch',
    description: 'An animated toggle switch component.',
    props: [
      { name: 'isOn', type: 'boolean', required: true, description: 'Switch state' },
      { name: 'onToggle', type: '(state: boolean) => void', required: true, description: 'State change callback' },
      { name: 'activeColor', type: 'string', required: false, description: 'Active color' },
      { name: 'inactiveColor', type: 'string', required: false, description: 'Inactive color', default: "'#E5E5EA'" },
    ],
    examples: [
      {
        title: 'Basic Switch',
        code: `const [isOn, setIsOn] = useState(false);
<Switch
  isOn={isOn}
  onToggle={(value) => setIsOn(value)}
/>`,
      },
      {
        title: 'With Label',
        code: `<View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
  <Text>Enable Notifications</Text>
  <Switch
    isOn={enabled}
    onToggle={setEnabled}
  />
</View>`,
      },
    ],
  },
  checkbox: {
    name: 'Checkbox',
    description: 'A customizable checkbox component with labels.',
    props: [
      { name: 'checked', type: 'boolean', required: true, description: 'Checked state' },
      { name: 'onChange', type: '() => void', required: true, description: 'Change callback' },
      { name: 'label', type: 'string', required: false, description: 'Label text' },
      { name: 'size', type: 'number', required: false, description: 'Icon size', default: '24' },
      { name: 'color', type: 'string', required: false, description: 'Checkbox color' },
    ],
    examples: [
      {
        title: 'Basic Checkbox',
        code: `const [checked, setChecked] = useState(false);
<CustomCheckbox
  checked={checked}
  onChange={() => setChecked(!checked)}
/>`,
      },
      {
        title: 'With Label',
        code: `<CustomCheckbox
  checked={checked}
  onChange={() => setChecked(!checked)}
  label="I agree to terms"
/>`,
      },
    ],
  },
  toast: {
    name: 'Toast',
    description: 'A non-intrusive notification component that appears at the top or bottom.',
    props: [
      { name: 'type', type: "'info' | 'success' | 'warning' | 'error'", required: true, description: 'Toast type' },
      { name: 'message', type: 'string', required: true, description: 'Toast message' },
      { name: 'heading', type: 'string', required: false, description: 'Optional heading' },
      { name: 'duration', type: 'number', required: false, description: 'Duration in ms', default: '3000' },
      { name: 'position', type: "'top' | 'bottom'", required: false, description: 'Position', default: "'top'" },
    ],
    examples: [
      {
        title: 'Success Toast',
        code: `Toast.show({
  type: 'success',
  message: 'Operation completed successfully!'
})`,
      },
      {
        title: 'Error Toast',
        code: `Toast.show({
  type: 'error',
  heading: 'Error',
  message: 'Something went wrong',
  duration: 5000
})`,
      },
    ],
  },
  chip: {
    name: 'Chip',
    description: 'Lightweight selection components for displaying options.',
    props: [
      { name: 'title', type: 'string', required: true, description: 'Chip text' },
      { name: 'type', type: "'contained' | 'outline'", required: false, description: 'Chip style', default: "'outline'" },
      { name: 'selected', type: 'boolean', required: false, description: 'Selection state', default: 'false' },
      { name: 'color', type: 'string', required: false, description: 'Chip color' },
      { name: 'onPress', type: '() => void', required: false, description: 'Press callback' },
    ],
    examples: [
      {
        title: 'Basic Chip',
        code: `<Chip
  title="Filter"
  onPress={() => {}}
/>`,
      },
      {
        title: 'Selected Chip',
        code: `<Chip
  title="Active"
  selected={true}
  type="contained"
  onPress={() => {}}
/>`,
      },
    ],
  },
  alert: {
    name: 'Alert',
    description: 'A modal alert component for user attention.',
    props: [
      { name: 'title', type: 'string', required: false, description: 'Alert title' },
      { name: 'message', type: 'string', required: false, description: 'Alert message' },
      { name: 'type', type: "'info' | 'success' | 'warning' | 'error'", required: false, description: 'Alert type' },
      { name: 'buttons', type: 'AlertButton[]', required: false, description: 'Action buttons' },
    ],
    examples: [
      {
        title: 'Simple Alert',
        code: `Alert.show({
  title: 'Confirm',
  message: 'Do you want to continue?',
  buttons: [
    { text: 'Cancel', onPress: () => {} },
    { text: 'OK', onPress: () => console.log('Confirmed') }
  ]
})`,
      },
      {
        title: 'Delete Confirmation',
        code: `Alert.show({
  title: 'Delete Item',
  message: 'This action cannot be undone.',
  type: 'warning',
  buttons: [
    { text: 'Cancel', style: 'cancel' },
    { text: 'Delete', style: 'destructive' }
  ]
})`,
      },
    ],
  },
  dialog: {
    name: 'Dialog',
    description: 'A customizable dialog/modal component.',
    props: [
      { name: 'title', type: 'string', required: false, description: 'Dialog title' },
      { name: 'message', type: 'string', required: false, description: 'Dialog message' },
      { name: 'buttons', type: 'DialogButton[]', required: false, description: 'Action buttons' },
    ],
    examples: [
      {
        title: 'Basic Dialog',
        code: `Dialog.show({
  title: 'Choose Action',
  message: 'What would you like to do?',
  buttons: [
    { text: 'Cancel', onPress: () => Dialog.hide() },
    { text: 'Confirm', onPress: () => {} }
  ]
})`,
      },
    ],
  },
  bottomsheet: {
    name: 'BottomSheet',
    description: 'A customizable bottom sheet component with smooth animations.',
    props: [
      { name: 'children', type: 'ReactNode', required: true, description: 'Sheet content' },
      { name: 'height', type: 'number', required: false, description: 'Sheet height' },
      { name: 'onOpen', type: '() => void', required: false, description: 'Open callback' },
      { name: 'onDismiss', type: '() => void', required: false, description: 'Close callback' },
    ],
    examples: [
      {
        title: 'Basic Bottom Sheet',
        code: `const ref = useRef(null);
<BottomSheet ref={ref} height={300}>
  <View style={{ padding: 20 }}>
    <Text>Bottom Sheet Content</Text>
  </View>
</BottomSheet>

<Button
  title="Show"
  onPress={() => ref.current?.show()}
/>`,
      },
    ],
  },
  divider: {
    name: 'Divider',
    description: 'A simple horizontal divider component.',
    props: [
      { name: 'height', type: 'number', required: false, description: 'Divider height', default: '1' },
    ],
    examples: [
      {
        title: 'Default Divider',
        code: `<Divider />`,
      },
      {
        title: 'Custom Height',
        code: `<Divider height={2} />`,
      },
    ],
  },
  screenwrapper: {
    name: 'ScreenWrapper',
    description: 'A wrapper component that handles background color from theme.',
    props: [
      { name: 'children', type: 'ReactNode', required: true, description: 'Screen content' },
      { name: 'style', type: 'ViewStyle', required: false, description: 'Custom style' },
    ],
    examples: [
      {
        title: 'Basic Usage',
        code: `<ScreenWrapper>
  <Text mode="bold" fontSize="large">
    Screen Title
  </Text>
</ScreenWrapper>`,
      },
    ],
  },
  animatedinput: {
    name: 'AnimatedInput',
    description: 'A text input with animated floating label.',
    props: [
      { name: 'value', type: 'string', required: true, description: 'Input value' },
      { name: 'placeholder', type: 'string', required: true, description: 'Floating label' },
      { name: 'onChangeText', type: '(text: string) => void', required: true, description: 'Change callback' },
      { name: 'multiline', type: 'boolean', required: false, description: 'Multiple lines' },
    ],
    examples: [
      {
        title: 'Basic Animated Input',
        code: `const [email, setEmail] = useState('');
<AnimatedInput
  value={email}
  placeholder="Email"
  onChangeText={setEmail}
/>`,
      },
    ],
  },
  buttongroup: {
    name: 'ButtonGroup',
    description: 'An animated button group for selection.',
    props: [
      { name: 'buttons', type: 'string[]', required: true, description: 'Button labels' },
      { name: 'onClick', type: '(index: number) => void', required: true, description: 'Selection callback' },
      { name: 'color', type: 'string', required: false, description: 'Active color' },
      { name: 'value', type: 'number', required: false, description: 'Initial selected', default: '0' },
    ],
    examples: [
      {
        title: 'Tab Selection',
        code: `const [selected, setSelected] = useState(1);
<ButtonGroup
  buttons={['Tab 1', 'Tab 2', 'Tab 3']}
  onClick={(index) => setSelected(index)}
  value={selected - 1}
/>`,
      },
    ],
  },
  selector: {
    name: 'Selector',
    description: 'A popover selector component for dropdown selections.',
    props: [
      { name: 'items', type: 'SelectItem[]', required: true, description: 'Array of options' },
      { name: 'selectedItem', type: 'SelectItem', required: false, description: 'Selected item' },
      { name: 'onItemSelect', type: '(item: SelectItem) => void', required: false, description: 'Selection callback' },
    ],
    examples: [
      {
        title: 'Basic Selector',
        code: `const [selected, setSelected] = useState(null);
const options = [
  { label: 'Option A', value: 'a' },
  { label: 'Option B', value: 'b' }
];

<Selector
  items={options}
  selectedItem={selected}
  onItemSelect={setSelected}
/>`,
      },
    ],
  },
};

function ComponentPage({ component, onNavigate }) {
  const [copied, setCopied] = useState(null);

  if (!component) {
    return (
      <div className="component-page">
        <div className="empty-state">
          <h2>Select a component</h2>
          <p>Choose a component from the sidebar to view its documentation</p>
        </div>
      </div>
    );
  }

  const compData = COMPONENTS_DATA[component.id];

  if (!compData) {
    return (
      <div className="component-page">
        <div className="empty-state">
          <h2>Documentation coming soon</h2>
          <p>Documentation for {component.name} is coming soon</p>
        </div>
      </div>
    );
  }

  const copyToClipboard = (code, exampleIndex) => {
    navigator.clipboard.writeText(code);
    setCopied(exampleIndex);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="component-page">
      <div className="component-header">
        <h1>{compData.name}</h1>
        <p className="component-category">{component.category}</p>
      </div>

      <section className="component-section">
        <h2>Description</h2>
        <p>{compData.description}</p>
      </section>

      <section className="component-section">
        <h2>Props</h2>
        <div className="props-table-container">
          <table className="props-table">
            <thead>
              <tr>
                <th>Prop</th>
                <th>Type</th>
                <th>Required</th>
                <th>Default</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {compData.props.map((prop, idx) => (
                <tr key={idx}>
                  <td><code>{prop.name}</code></td>
                  <td><code>{prop.type}</code></td>
                  <td>{prop.required ? '✓' : '✗'}</td>
                  <td>{prop.default ? <code>{prop.default}</code> : '-'}</td>
                  <td>{prop.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="component-section">
        <h2>Examples</h2>
        <div className="examples-container">
          {compData.examples.map((example, idx) => (
            <div key={idx} className="example-block">
              <div className="example-header">
                <h3>{example.title}</h3>
                <button
                  className="copy-btn"
                  onClick={() => copyToClipboard(example.code, idx)}
                >
                  {copied === idx ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <CodeBlock
                language="jsx"
                code={example.code}
              />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export default ComponentPage;
